
import React, { useMemo, useState } from "react";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

function OptimalContentFrequencyChart({
    title,
    datasets = [],
    previousdatasets = [],
    isLoading = false,
    isEntitled = true,
    featureName = "",
    planRequired = "",
    entitlements // ✅ Added prop
}) {

    // ---------------------------------------------------------------------
    // Safe datasets guard
    // ---------------------------------------------------------------------
    const safeDatasets = Array.isArray(datasets) ? datasets : [];

    // ---------------------------------------------------------------------
    // Memo: detect whether content exists
    // ---------------------------------------------------------------------
    const hasContent = useMemo(() => {
        return safeDatasets.some(
            (profile) =>
                profile?.data &&
                profile.data.some(
                    (day) =>
                        (day.posts && day.posts.length > 0) ||
                        (day.videos && day.videos.length > 0)
                )
        );
    }, [safeDatasets]);

    // ---------------------------------------------------------------------
    // Extraction Logic
    // ---------------------------------------------------------------------
    const getPostEngagement = (post, platform) => {
        if (platform === "YouTube") {
            const vm = post.videoMetrics || {};
            return (vm.likeCount || 0) + (vm.commentCount || 0);
        } else {
            const pm = post.postMetrics || {};
            return (
                (pm.likes || pm.like || 0) +
                (pm.comments || 0) +
                (pm.shares || 0) +
                (pm.love || 0) +
                (pm.wow || 0) +
                (pm.haha || 0) +
                (pm.sad || 0) +
                (pm.angry || 0)
            );
        }
    };

    // ---------------------------------------------------------------------
    // Extraction Helper
    // ---------------------------------------------------------------------
    const getAllPostsWithDates = (profile) => {
        const allPosts = [];
        if (profile.data) {
            profile.data.forEach(dayItem => {
                const date = dayItem.date || dayItem.datetime; // fallback
                if (dayItem.posts) {
                    dayItem.posts.forEach(p => allPosts.push({ ...p, date: date, platform: profile.platform }));
                }
                if (dayItem.videos) {
                    dayItem.videos.forEach(v => allPosts.push({ ...v, date: v.publishedAt || date, platform: profile.platform }));
                }
            });
        }
        return allPosts;
    };

    // ---------------------------------------------------------------------
    // Dynamic Label Generator (Adaptive Binning)
    // ---------------------------------------------------------------------
    const dynamicLabels = useMemo(() => {
        // 1. Gather all weekly frequencies globally to find Max
        let maxFreq = 0;

        safeDatasets.forEach(profile => {
            const allPosts = getAllPostsWithDates(profile);
            const weeklyGroups = {};
            allPosts.forEach(post => {
                const weekKey = dayjs(post.date).startOf('isoWeek').format("YYYY-MM-DD");
                if (!weeklyGroups[weekKey]) weeklyGroups[weekKey] = 0;
                weeklyGroups[weekKey]++;
            });

            Object.values(weeklyGroups).forEach(count => {
                if (count > maxFreq) maxFreq = count;
            });
        });

        // 2. Base Buckets
        const baseLabels = ["1-2", "3-5", "6-9"];

        // 3. Determine Dynamic Step Size
        // Goal: Approx 15 buckets total. Base = 3. Dynamic Slots = 12.
        // Range to cover: [10, maxFreq]
        let step = 5;
        if (maxFreq >= 10) {
            const range = maxFreq - 10;
            // availableSlots = 12.
            // minStep = range / 12. 
            // e.g. Max=70 -> range=60. 60/12 = 5. Step 5 is good.
            // e.g. Max=200 -> range=190. 190/12 = 15.8. Needs step 20.

            const rawStep = Math.ceil(range / 12);

            // Snap to nice increment
            const niceSteps = [5, 10, 15, 20, 25, 30, 40, 50, 100];

            if (rawStep > 5) {
                step = 100; // default cap
                for (let s of niceSteps) {
                    if (s >= rawStep) {
                        step = s;
                        break;
                    }
                }
                // If larger than 100, just use raw integer step (rounded up to 10 maybe?)
                if (rawStep > 100) {
                    step = Math.ceil(rawStep / 10) * 10;
                }
            }
        }

        // 4. Generate Candidates (NO FILTERING)
        const finalLabels = [...baseLabels];

        if (maxFreq >= 10) {
            let start = 10;
            // Generate buckets covering up to maxFreq
            // e.g. if maxFreq is 72 and step is 5: 
            // 10-14, ..., 70-74. loop until start > maxFreq? No, until end >= maxFreq.

            while (start <= maxFreq) {
                const end = start + step - 1;
                finalLabels.push(`${start}-${end}`);
                start += step;
            }
        } else {
            // If maxFreq < 10, technically we just rely on baseLabels. 
            // But we might want to check if data < 10 exists? 
            // The prompt implies we always keep structure.
        }

        return finalLabels;
    }, [safeDatasets]);


    const calculateOptimalFrequency = (profile, labels) => {
        const allPosts = getAllPostsWithDates(profile);

        // 2. Group by Week
        const weeklyGroups = {};
        allPosts.forEach(post => {
            const weekKey = dayjs(post.date).startOf('isoWeek').format("YYYY-MM-DD");
            if (!weeklyGroups[weekKey]) weeklyGroups[weekKey] = [];
            weeklyGroups[weekKey].push(post);
        });

        // 3. Init Buckets
        const buckets = {};
        labels.forEach(l => {
            buckets[l] = { totalAvgEng: 0, weeksCount: 0 };
        });

        // 4. Assign to Buckets
        Object.values(weeklyGroups).forEach(postsInWeek => {
            const freq = postsInWeek.length;
            if (freq === 0) return;

            // Find matching bucket
            let bucketName = null;
            for (let label of labels) {
                const parts = label.split("-");
                const min = parseInt(parts[0], 10);
                const max = parts.length === 2 ? parseInt(parts[1], 10) : 999999;
                if (freq >= min && freq <= max) {
                    bucketName = label;
                    break;
                }
            }

            if (bucketName && buckets[bucketName]) {
                const totalEngInWeek = postsInWeek.reduce((sum, p) => sum + getPostEngagement(p, profile.platform), 0);
                const avgEngPerPost = totalEngInWeek / freq;

                buckets[bucketName].totalAvgEng += avgEngPerPost;
                buckets[bucketName].weeksCount += 1;
            }
        });

        const data = labels.map(label => {
            const b = buckets[label];
            return b && b.weeksCount > 0 ? (b.totalAvgEng / b.weeksCount) : 0;
        });

        return { labels, data, buckets };
    };


    // ---------------------------------------------------------------------
    // Chart Data Construction
    // ---------------------------------------------------------------------
    const chartData = useMemo(() => {
        return {
            labels: dynamicLabels,
            datasets: safeDatasets.map(profile => {
                const res = calculateOptimalFrequency(profile, dynamicLabels);
                return {
                    label: profile.name || profile.label || "Profile",
                    data: res.data,
                    profileId: profile.profileId,
                    profilePictureUrl: profile.profilePictureUrl,
                    platform: profile.platform,
                    metricName: "Optimal Frequency"
                };
            })
        };
    }, [safeDatasets, dynamicLabels]);


    // ---------------------------------------------------------------------
    // Metrics (Identify Sweet Spot)
    // ---------------------------------------------------------------------
    const metrics = safeDatasets.map(profile => {
        // 1. Current Optimal Frequency
        const { data: currentData, labels } = calculateOptimalFrequency(profile, dynamicLabels);

        // Find index of max
        let maxVal = -1;
        let maxIdx = -1;
        currentData.forEach((val, idx) => {
            if (val > maxVal) {
                maxVal = val;
                maxIdx = idx;
            }
        });

        const optimalFreq = maxVal > 0 ? labels[maxIdx] : "N/A";

        // 2. Previous Optimal Frequency
        let previousOptimalFreq = "N/A";
        const previousProfile = Array.isArray(previousdatasets)
            ? previousdatasets.find(p => p.profileId === profile.profileId)
            : null;

        if (previousProfile) {
            const { data: prevData } = calculateOptimalFrequency(previousProfile, dynamicLabels);
            let prevMaxVal = -1;
            let prevMaxIdx = -1;
            prevData.forEach((val, idx) => {
                if (val > prevMaxVal) {
                    prevMaxVal = val;
                    prevMaxIdx = idx;
                }
            });
            previousOptimalFreq = prevMaxVal > 0 ? labels[prevMaxIdx] : "N/A";
        }

        return {
            profilePic: profile.profilePictureUrl || "https://via.placeholder.com/150",
            name: profile.name || "Unknown",
            platform: profile.platform,
            metricLabel: "Optimal Content Frequency",
            currentMetricValue: optimalFreq === "N/A" ? "N/A" : optimalFreq.replace("-", " to "),
            previousMetricValue: previousOptimalFreq === "N/A" ? "N/A" : previousOptimalFreq.replace("-", " to "),
            isQualitative: true // 🚩 Tell MetricCard not to show percentage change
        };
    });


    // ---------------------------------------------------------------------
    // Loading State
    // ---------------------------------------------------------------------
    if (isLoading) {
        return (
            <Card>
                <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="250px">
                    <CircularProgress size={32} thickness={4} color="accent" />
                </MDBox>
            </Card>
        );
    }

    // ---------------------------------------------------------------------
    // Tooltip Description
    // ---------------------------------------------------------------------
    // ---------------------------------------------------------------------
    // Tooltip Description
    // ---------------------------------------------------------------------
    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" gutterBottom display="block">
                This chart analyses your weekly posting habits to identify the "Sweet Spot" frequency
                that maximises audience engagement per post.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" gutterBottom display="block">
                - <strong>X-Axis (Frequency)</strong>: Number of posts published per week (e.g., 3-5).<br />
                - <strong>Y-Axis (Engagement)</strong>: Average engagement per post for that frequency.<br />
                - <strong>Tallest Bar</strong>: Indicates your most effective posting volume.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>Insights you can gain:</strong>
            </Typography>
            <Typography variant="caption" display="block">
                - Determine if posting more often dilutes your engagement. <br />
                - Find out if you are over-posting or under-posting. <br />
                - Adjust your content calendar to match the optimal frequency.
            </Typography>
        </div>
    );

    return (
        <VerticalBarChart
            title={title}
            description={tooltipContent}
            height="19.125rem"
            chart={chartData}
            metrics={metrics}
            is_derived={true} // Use derived metrics card style
            scaleType="category" // 🛑 Fix for axis labels
            isEntitled={isEntitled}       // ✅ Passed prop
            featureName={featureName}     // ✅ Passed prop
            planRequired={planRequired}   // ✅ Passed prop
            rawData={safeDatasets}
            entitlements={entitlements} // ✅ Pass entitlements
            disableToggle={true} // ✅ Disable toggle for this chart
            yAxisTitle="Average Engagement per Post" // ✅ Correct Y-Axis Title
        />
    );
}

OptimalContentFrequencyChart.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    datasets: PropTypes.array,
    previousdatasets: PropTypes.array,
    isLoading: PropTypes.bool,
    isEntitled: PropTypes.bool,
    featureName: PropTypes.string,
    planRequired: PropTypes.string,
    entitlements: PropTypes.object,
};

export default OptimalContentFrequencyChart;
